<template>
  <panel-layout
    id="order-panel"
    :loading="loading"
    :show-footer="false"
    :shouldDismiss="() => !unsavedChanges"
    :didAttemptToDismiss="cancel"
    footer-class="bg-gray-50 border-t px-6 py-3 dark:border-gray-800 dark:bg-gray-900"
  >
    <template #actions-left>
      <div v-if="order" class="flex items-center space-x-1">
        <o-button
          v-if="!isClosed"
          flat
          size="sm"
          icon="edit"
          @click="handleEditOrder"
        >
          {{ $t('general.edit') }}
        </o-button>
        <o-button
          v-if="!isClosed"
          flat
          size="sm"
          icon="orderClose"
          @click="handleCloseOrder"
        >
          {{ $t('order.close') }}
        </o-button>
        <o-button
          v-if="isClosed"
          flat
          size="sm"
          icon="orderClose"
          @click="handleOpenOrder"
        >
          {{ $t('pages.order.id.open') }}
        </o-button>
      </div>
    </template>

    <template #actions-right>
      <div v-if="order" class="flex items-center space-x-1">
        <o-tooltip v-if="!isClosed" class="h-8" :text="$t('order.editTip')">
          <o-button flat icon="saleTip" @click="handleEditTip" />
        </o-tooltip>

        <o-tooltip class="h-8" :text="$t('pages.order.id.print')">
          <o-button flat icon="print" @click="handlePrintReceipt" />
        </o-tooltip>

        <o-tooltip class="h-8" :text="$t('pages.order.id.email')">
          <o-button flat icon="email" @click="handleEmailOrder" />
        </o-tooltip>

        <o-dropdown placement="bottom-end">
          <o-button flat class="block" icon="ellipses" />

          <template #content>
            <o-dropdown-item v-if="!isClosed" icon="edit" @click="handleEditOrder">
              {{ $t('order.editDetails') }}
            </o-dropdown-item>
            <o-dropdown-item v-if="!isClosed" icon="orderClose" @click="handleCloseOrder">
              {{ $t('order.close') }}
            </o-dropdown-item>
            <o-dropdown-item v-if="isClosed" icon="orderClose" @click="handleOpenOrder">
              {{ $t('pages.order.id.open') }}
            </o-dropdown-item>
            <o-dropdown-item v-if="order.customer" icon="customer" tag-name="o-panel-link" :path="`/customer/panel?id=${order.customer.idCustomer}`">
              {{ $t('pages.order.id.viewCustomer') }}
            </o-dropdown-item>
            <o-dropdown-item icon="print" @click="handlePrintReceipt">
              {{ $t('general.print') }}
            </o-dropdown-item>
            <o-dropdown-item icon="email" @click="handleEmailOrder">
              {{ $t('general.email') }}
            </o-dropdown-item>

            <o-dropdown-divider />

            <o-history-link :item="order" model="Order" component="ODropdownItem" icon="history" />

            <o-dropdown-divider />

            <o-dropdown-item icon="delete" variant="danger" @click="handleDeleteOrder">
              {{ $t('general.delete') }}
            </o-dropdown-item>
          </template>
        </o-dropdown>
      </div>
    </template>

    <template #heading>
      <div v-if="order" class="flex justify-between items-center">
        <div>
          <div class="flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-x-2 sm:space-y-0 mr-3">
            <h3 class="text-xl tracking-tight font-bold leading-none">
              {{ $t('pages.order.id.label',{ customerLabel }) }}
            </h3>
            <div class="flex flex-wrap items-center -mb-1">
              <template v-for="segment in order.customer.segments" :key="segment.idSegment">
                <o-chip

                  custom
                  size="sm"
                  :variant="segment.variant"
                  class="mb-1 mr-1"
                >
                  {{ segment.shortName }}
                </o-chip>
              </template>
            </div>
          </div>

          <p class="opacity-70 mt-1">
            {{ orderLabel }} - {{ dateLabel }}
          </p>

          <div class="mt-3 flex items-center gap-1">
            <o-chip v-if="isOverpaid" variant="warning">
              {{ $t('order.overpaid') }}
            </o-chip>
            <o-chip v-else-if="!isPaid && Number(order.paid)" variant="warning">
              {{ $t('order.partiallyPaid') }}
            </o-chip>
            <o-chip v-else-if="isPaid" variant="success" size="sm">
              {{ $t('general.paid') }}
            </o-chip>
            <o-chip :variant="isClosed ? 'success' : 'warning'" size="sm">
              {{ isClosed ? $t('general.closed') : $t('general.open') }}
            </o-chip>
            <o-chip v-if="hasEmployee" outline flat size="sm">
              {{ employeeLabel }}
            </o-chip>
            <barcode
              v-model:order="order"
              v-model:loading="loading"
            />
          </div>
        </div>
        <div>
          <div class="flex flex-col items-end md:min-w-40 gap-2">
            <p class="text-xs opacity-70 uppercase tracking-wider">
              {{ isPaid ? $t('pages.order.id.paid') : $t('pages.order.id.due') }}
            </p>
            <div class="leading-tight">
              <p class="text-xl tracking-tight font-bold opacity-80">
                {{ isPaid ? paidLabel : remainderLabel }}
              </p>
            </div>

            <o-button v-if="!isPaid && !isClosed" variant="success" size="sm" @click="handleAddPayment">
              {{ $t('pages.order.id.payNow') }}
            </o-button>
          </div>
        </div>
      </div>

      <div v-if="order && order.customFields" class="mt-1 flex items-center flex-wrap">
        <!--<o-description-details
          v-for="field in order.customFields"
          :key="field.id"
          :term="field.name"
          class="mb-1"
          padding="0"
        >
          {{ field.valueLabel }}
        </o-description-details>-->

        <o-chip
          v-for="field in order.customFields"
          :key="field.id"
          outline
          flat
          size="sm"
          class="mb-1 mr-1"
        >
          {{ $t('general.colonSeparator', { label1: field.name, label2: field.valueLabel || '-' }) }}
        </o-chip>
      </div>
    </template>

    <div v-if="order" class="h-full">
      <o-scroll class="h-full">
        <div class="divide-y dark:divide-gray-800">
          <order-section
            v-for="(section, key) in order.data.items"
            :key="key"
            v-model:order="order"
            :section-key="key"
          />

          <order-section
            v-model:order="order"
            section-key="paymentRequests"
          />

          <o-collapse
            :visible="true"
            :clickable="false"
            header-class="flex flex-row-reverse items-center w-full px-6 py-3 cursor-pointer"
            class="bg-white hover:bg-gray-50 dark:bg-gray-900 transition-all border-b"
          >
            <template #header>
              <div class="flex-grow flex items-center justify-between ml-4">
                <h4 class="font-medium opacity-80 capitalize">
                  {{ $t('general.summary') }}
                </h4>
              </div>
            </template>

            <template #toggle="{ toggle }">
              <o-button icon="accordion" size="sm" flat @click="toggle" />
            </template>

            <div class="flex items-end my-2 mb-6 px-6">
              <div class="w-1/2 mr-8 relative border rounded-md overflow-hidden bg-white dark:bg-gray-800/50 dark:border-gray-800">
                <text-area-field v-model="order.notes" :placeholder="$t('pages.order.id.notes')" base-class="text-sm w-full dark:bg-gray-800/50" rows="4" />
                <div v-if="!isClosed" class="w-full p-1 border-t bg-gray-50 dark:bg-gray-900">
                  <o-button size="sm" flat outline @click="save()">
                    {{ $t('pages.order.id.saveNotes') }}
                  </o-button>
                </div>
              </div>
              <totals :order="order" class="w-1/2" @editTip="handleEditTip" />
            </div>
          </o-collapse>
        </div>
      </o-scroll>
    </div>

    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :portal="panel.portal"
      :options="[
        { text: $t('general.back'), type: 'cancel'},
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save(true) }
      ]"
    />

    <o-dialog
      ref="customerCreditAlertDialog"
      :title="$t('pages.order.id.unpaidCredit')"
      :info="$t('pages.order.id.unpaidCreditInfo')"
      :options="[
        { text: $t('general.cancel'), type: 'cancel' },
        { text: $t('pages.order.id.proceed'), type: 'confirm' }
      ]"
    />

    <template v-if="order">
      <print-receipt-modal
        ref="printReceiptModal"
        v-model:order="order"
      />

      <edit-order-dialog
        ref="editOrderDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <close-order-dialog
        ref="closeOrderDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
        @success="handleCloseOrderSuccess"
      />

      <delete-order-dialog
        ref="deleteOrderDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
        @success="handleDeleteOrderSuccess"
      />

      <refund-payment-dialog
        ref="refundPaymentDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <delete-item-dialog
        ref="deleteItemDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <edit-item-dialog
        ref="editItemDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <edit-tip-dialog
        ref="editTipDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-reservations-dialog
        ref="addReservationsDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-service-dialog
        ref="addServiceDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-product-dialog
        ref="addProductDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-customer-credit-dialog
        ref="addCustomerCreditDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-gift-cards-dialog
        ref="addGiftCardsDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
        @create="handleCreateGiftCard"
      />

      <add-passes-dialog
        ref="addPassesDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
        @create="handleCreateCustomerPass"
      />

      <add-customer-courses-dialog
        v-if="canUseCourses"
        ref="addCustomerCoursesDialog"
        :portal="panel.portal"
        :order="order"
        :loading="loading"
        @update:order="order = $event"
        @update:loading="loading = $event"
        @create="handleCreateCustomerCourse"
      />

      <add-charge-dialog
        ref="addChargeDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-discount-dialog
        ref="addDiscountDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <add-payment-modal
        ref="addPaymentModal"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
        @close="handleCloseOrder"
      />

      <request-online-payment-dialog
        ref="requestOnlinePaymentDialog"
        :portal="panel.portal"
        v-model:order="order"
        v-model:loading="loading"
      />

      <create-customer-pass-dialog
        ref="createCustomerPassDialog"
        :portal="panel.portal"
        @update:order="order = $event"
      />

      <create-gift-card-dialog
        ref="createGiftCardDialog"
        :portal="panel.portal"
        search
        @update:order="order = $event"
        @search="handleAddGiftCards"
      />
    </template>
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import Totals from '@/components/order/edit/Totals.vue'
// @ts-ignore
import OrderSection from '@/components/order/elements/OrderSection.vue'
// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'
// @ts-ignore
import EditOrderDialog from '@/components/order/actions/EditOrder.vue'
// @ts-ignore
import CloseOrderDialog from '@/components/order/actions/CloseOrder.vue'
// @ts-ignore
import DeleteOrderDialog from '@/components/order/actions/DeleteOrder.vue'
// @ts-ignore
import EditTipDialog from '@/components/order/actions/EditTip.vue'
// @ts-ignore
import RefundPaymentDialog from '@/components/order/actions/RefundPayment.vue'
// @ts-ignore
import DeleteItemDialog from '@/components/order/actions/DeleteItem.vue'
// @ts-ignore
import EditItemDialog from '@/components/order/actions/EditItem.vue'
// @ts-ignore
import AddReservationsDialog from '@/components/order/actions/AddReservations.vue'
// @ts-ignore
import AddServiceDialog from '@/components/order/actions/AddService.vue'
// @ts-ignore
import AddProductDialog from '@/components/order/actions/AddProduct.vue'
// @ts-ignore
import AddCustomerCreditDialog from '@/components/order/actions/AddCustomerCredit.vue'
// @ts-ignore
import AddGiftCardsDialog from '@/components/order/actions/AddGiftCards.vue'
// @ts-ignore
import AddPassesDialog from '@/components/order/actions/AddPasses.vue'
// @ts-ignore
import AddCustomerCoursesDialog from '@/components/order/actions/AddCustomerCourses.vue'
// @ts-ignore
import AddChargeDialog from '@/components/order/actions/AddCharge.vue'
// @ts-ignore
import AddDiscountDialog from '@/components/order/actions/AddDiscount.vue'
// @ts-ignore
import CreateCustomerPassDialog from '@/components/customerPass/actions/Add.vue'
// @ts-ignore
import CreateGiftCardDialog from '@/components/giftCard/actions/Add.vue'
// @ts-ignore
import PrintReceiptModal from '@/components/order/actions/PrintReceipt.vue'
// @ts-ignore
import AddPaymentModal from '@/components/order/actions/AddPayment.vue'
// @ts-ignore
import RequestOnlinePaymentDialog from '@/components/order/actions/RequestOnlinePayment.vue'
// @ts-ignore
import Barcode from '@/components/order/elements/Barcode.vue'

// @ts-ignore
import { TextAreaField } from '@/components/fields'

// @ts-ignore
import { ODropdownItem, ODropdownDivider, ODescriptionDetails } from '@/components/elements'

import { usePanel } from '~/composables/usePanel'

const props = defineProps<{ id?: string }>()

const { $api, $bus, $notification, $date, $currency, $i18n, $gates, $grants, $panel } = useNuxtApp()

const panel = usePanel()

const confirmDialog = ref<any>()
const customerCreditAlertDialog = ref<any>()
const editOrderDialog = ref<any>()
const closeOrderDialog = ref<any>()
const deleteOrderDialog = ref<any>()
const editTipDialog = ref<any>()
const refundPaymentDialog = ref<any>()
const deleteItemDialog = ref<any>()
const editItemDialog = ref<any>()
const addReservationsDialog = ref<any>()
const addServiceDialog = ref<any>()
const addProductDialog = ref<any>()
const addCustomerCreditDialog = ref<any>()
const addGiftCardsDialog = ref<any>()
const addPassesDialog = ref<any>()
const addCustomerCoursesDialog = ref<any>()
const addChargeDialog = ref<any>()
const addDiscountDialog = ref<any>()
const addPaymentModal = ref<any>()
const requestOnlinePaymentDialog = ref<any>()
const printReceiptModal = ref<any>()
const createCustomerPassDialog = ref<any>()
const createGiftCardDialog = ref<any>()

const order = ref<any>(null)
const loading = ref(true)

const { unsavedChanges, snapshot } = useUnsavedChanges(order)

const customerLabel = computed(() => order.value.customer.displayName)
const dateLabel = computed(() => {
  const d = $date.local(new Date(order.value.date * 1000))
  return $date.format(d, $date.presets.full)
})
const employeeLabel = computed(() => order.value.employee.displayName)
const orderLabel = computed(() => `#${order.value.orderNumber}`)
const currencySymbol = computed(() => order.value.data.currencySymbol)
const remainderLabel = computed(() => $currency.format(order.value.data.totals.remainder))
const paidLabel = computed(() => $currency.format(order.value.data.totals.payments))
const totalLabel = computed(() => $currency.format(order.value.data.totals.total))
const hasItems = computed(() => order.value.hasItems)
const hasRemaining = computed(() => order.value.hasRemaining)
const hasOverpayment = computed(() => order.value.hasOverpayment)
const hasCustomerCredit = computed(() => order.value.hasCustomerCredit)
const hasEmployee = computed(() => !!order.value.employee)
const isPaid = computed(() => order.value.isPaid)
const isClosed = computed(() => order.value.closed)
const isOverpaid = computed(() => order.value.isOverpaid)
const canUseCourses = computed(() =>
  $gates.get('courses') && $grants.all({ course: ['view'] })
)

const handleDeleteItem = (item: any) => { deleteItemDialog.value.open(item) }
const handleEditItem = (item: any) => { editItemDialog.value.open(item) }
const handleRefundPayment = (item: any) => { refundPaymentDialog.value.open(item) }
const handleAddReservations = () => { addReservationsDialog.value.open() }
const handleAddService = () => { addServiceDialog.value.open() }
const handleAddProduct = () => { addProductDialog.value.open() }
const handleAddCustomerCredit = () => { addCustomerCreditDialog.value.open() }
const handleAddGiftCards = () => { addGiftCardsDialog.value.open(false) }
const handleAddPasses = () => { addPassesDialog.value.open() }
const handleAddCustomerCourses = () => {
  if (canUseCourses.value) {
    addCustomerCoursesDialog.value?.open()
  }
}
const handleAddCharge = () => { addChargeDialog.value.open() }
const handleAddDiscount = () => { addDiscountDialog.value.open() }
const handleAddPayment = () => { if (!isClosed.value) { addPaymentModal.value.open() } }
const handleRequestOnlinePayment = () => { requestOnlinePaymentDialog.value.open() }

const handleCreateCustomerPass = () => {
  const o = order.value
  const customer = o.customer
  createCustomerPassDialog.value.open({ customer, order: o })
}

const handleCreateCustomerCourse = () => {
  if (!canUseCourses.value || !$grants.all({ customerCourse: ['create'] })) return

  const currentOrder = order.value
  $panel.create('/customer/course/add', {
    props: {
      idOrder: String(currentOrder.idOrder),
      presets: { customer: currentOrder.customer },
    },
    events: {
      'update:order': (updatedOrder: any) => {
        order.value = updatedOrder
      },
    },
  })
}

const handleCreateGiftCard = () => {
  const o = order.value
  createGiftCardDialog.value.open({ order: o })
}

provide('handleDeleteItem', handleDeleteItem)
provide('handleEditItem', handleEditItem)
provide('handleRefundPayment', handleRefundPayment)
provide('handleAddReservations', handleAddReservations)
provide('handleAddService', handleAddService)
provide('handleAddProduct', handleAddProduct)
provide('handleAddCustomerCredit', handleAddCustomerCredit)
provide('handleAddGiftCards', handleAddGiftCards)
provide('handleCreateGiftCard', handleCreateGiftCard)
provide('handleAddPasses', handleAddPasses)
provide('handleAddCustomerCourses', handleAddCustomerCourses)
provide('handleAddCharge', handleAddCharge)
provide('handleAddDiscount', handleAddDiscount)
provide('handleAddPayment', handleAddPayment)
provide('handleRequestOnlinePayment', handleRequestOnlinePayment)

watch(order, () => {
  loading.value = true
  snapshot()
  loading.value = false

  $bus.emit.updated('Order', order.value)

  const reservations = order.value?.data?.items?.reservations

  if (reservations) {
    for (const reservation of reservations) {
      $bus.emit.updated('Reservation', reservation)
    }
  }
})

const handleEditOrder = () => { editOrderDialog.value.open() }

const handleCloseOrder = async () => {
  if (!isPaid.value && hasCustomerCredit.value) {
    const response = await customerCreditAlertDialog.value.open()
    if (response === 'confirm') {
      closeOrderDialog.value.open()
    }
  } else {
    closeOrderDialog.value.open()
  }
}

const handleOpenOrder = async () => {
  loading.value = true
  try {
    const result = (await $api.order.open(order.value.idOrder.toString())).data
    order.value = result
    $notification.toast({
      title: $i18n.t('pages.order.id.openSuccess'),
      text: $i18n.t('pages.order.id.openSuccessInfo', { number: order.value.orderNumber }),
      variant: 'success',
    })
  } catch (e) { console.log(e) }
  loading.value = false
}

const handlePrintReceipt = () => { printReceiptModal.value.open() }

const handleEmailOrder = async () => {
  loading.value = true
  try {
    const success = await $api.order.email(order.value.idOrder.toString())
    if (success) {
      $notification.toast({
        title: $i18n.t('message.emailSent'),
        text: $i18n.t('pages.order.id.sentSuccessInfo', { number: order.value.orderNumber }),
        variant: 'success',
      })
    }
  } catch (e) { console.log(e) }
  loading.value = false
}

const handleDeleteOrder = () => { deleteOrderDialog.value.open() }
const handleEditTip = () => { editTipDialog.value.open() }

const handleDeleteOrderSuccess = () => {
  $bus.emit.deleted('Order', order.value)
  cancel(true)
}

const handleCloseOrderSuccess = async (print: boolean) => {
  if (print) {
    await printReceiptModal.value.open()
  } else {
    cancel(true)
  }
}

const save = async (close = false) => {
  loading.value = true
  try {
    const result = (await $api.order.save(order.value)).data
    if (result) {
      order.value = result
      $notification.toast({
        title: $i18n.t('pages.order.id.saveSuccess'),
        text: $i18n.t('pages.order.id.saveSuccessInfo'),
        variant: 'success',
      })
      if (close) { panel.close(true) }
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
  loading.value = false
}

const cancel = (force = false) => {
  if (!force && unsavedChanges.value) {
    confirmDialog.value.open()
  } else {
    panel.close(force)
  }
}

const init = async (id?: string) => {
  loading.value = true
  try {
    if (id) {
      order.value = (await $api.order.get(id)).data
      snapshot()
    }
  } catch (e) { console.log(e) }
  loading.value = false
}

watch(() => props.id, (id) => init(id), { immediate: true })

onMounted(() => {
  document?.activeElement?.blur?.()
})
</script>
